// MaturaMeanScaledABCClosedTask.js
// Zadanie zamknięte – średnia arytmetyczna a,b,c i ich wielokrotności

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";
import { shuffle } from "../../../../utils/shuffle";

// ================== HELPERS ==================

const randInt = (a, b) =>
  Math.floor(Math.random() * (b - a + 1)) + a;

function pickTwoDistinct(arr) {
  const a = arr[randInt(0, arr.length - 1)];
  let b = a;
  while (b === a) {
    b = arr[randInt(0, arr.length - 1)];
  }
  return [a, b];
}

// ================== GENERATOR ==================

function generateJune2025Original() {
  const meanABC = 12;
  const k1 = 2;
  const k2 = 3;
  const correct = (meanABC * (k1 + k2)) / 2;
  const values = [10, 12, 30, 60];
  const options = values.map((value, index) => ({
    label: String.fromCharCode(65 + index),
    latex: String(value),
    value,
    isCorrect: value === correct,
  }));

  return {
    type: "MaturaMeanScaledABCClosedTask",
    data: {
      meanABC,
      k1,
      k2,
      correct,
      options,
      correctIndex: options.findIndex((option) => option.isCorrect),
      termsLatex: "2a,\\; 3a,\\; 2b,\\; 3b,\\; 2c,\\; 3c",
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_CZE",
      taskNumber: 29,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_CZE"
  ) {
    return generateJune2025Original();
  }

  const factorsBase = [2, 3, 4, 5];

  let meanABC;
  let k1;
  let k2;
  let correct;

  // losujemy tak długo, aż wynik będzie ładną liczbą całkowitą
  while (true) {
    [k1, k2] = pickTwoDistinct(factorsBase);
    const sumFactors = k1 + k2;

    // średnia a,b,c
    meanABC = randInt(4, 20);

    if (!Number.isInteger(meanABC * sumFactors / 2)) continue;

    correct = (meanABC * sumFactors) / 2;

    if (correct > 0 && correct <= 200) break;
  }

  // termy 2a,3a,2b,... w losowej kolejności
  const rawTerms = [
    `${k1}a`,
    `${k2}a`,
    `${k1}b`,
    `${k2}b`,
    `${k1}c`,
    `${k2}c`,
  ];
  const terms = shuffle(rawTerms);

  const termsLatex = terms.join(",\\; ");

  // ===== DYSTRKTORY =====

  const distractorSet = new Set();

  const addDistractor = (v) => {
    if (!Number.isFinite(v)) return;
    if (v <= 0) return;
    if (v === correct) return;
    distractorSet.add(v);
  };

  // 1. Uczeń uważa, że średnia się nie zmienia
  addDistractor(meanABC);

  // 2. Uczeń bierze tylko jeden współczynnik
  addDistractor(k1 * meanABC);
  addDistractor(k2 * meanABC);

  // 3. Uczeń źle dzieli – np. dzieli przez 3 zamiast przez 2
  const wrongFactor3 = (k1 + k2) / 3;
  if (Number.isInteger(wrongFactor3)) {
    addDistractor(wrongFactor3 * meanABC);
  }
  const wrongFactor4 = (k1 + k2) / 4;
  if (Number.isInteger(wrongFactor4)) {
    addDistractor(wrongFactor4 * meanABC);
  }

  // uzupełniamy „w okolicy” poprawnej odpowiedzi
  let step = 5;
  while (distractorSet.size < 3) {
    addDistractor(correct + step);
    addDistractor(Math.max(1, correct - step));
    step += 5;
  }

  const wrongValues = shuffle(Array.from(distractorSet)).slice(0, 3);

  const allValues = shuffle([correct, ...wrongValues]);

  const options = allValues.map((v, idx) => ({
    value: v,
    latex: `${v}`,
    isCorrect: v === correct,
    label: String.fromCharCode("A".charCodeAt(0) + idx),
  }));

  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaMeanScaledABCClosedTask",
    data: {
      meanABC,
      k1,
      k2,
      correct,
      options,
      correctIndex,
      termsLatex,
    },
  };
}

// ================== TREŚĆ ==================

export function renderTask({ data }) {
  const { meanABC, termsLatex, options } = data;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.6 }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>

      <p>
        Średnia arytmetyczna trzech liczb:{" "}
        <InlineMath math={"a,\\; b,\\; c"} /> jest równa{" "}
        <InlineMath math={String(meanABC)} />.
      </p>

      <p>
        Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.
      </p>

      <p>
        Średnia arytmetyczna sześciu liczb:{" "}
        <InlineMath math={termsLatex} /> jest równa:
      </p>

      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ================== INPUT ABCD ==================

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;

  const value =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
      value: opt.value,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={value}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

// ================== CHECK ==================

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") {
    return false;
  }
  const { correctIndex } = data;
  return userAnswer.choiceIndex === correctIndex;
}

// ================== POPRAWNA ODPOWIEDŹ ==================

export function renderAnswer({ data }) {
  const { options, correctIndex } = data;
  const correct = options?.[correctIndex];
  if (!correct) return null;

  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />
    </div>
  );
}

// ================== ODPOWIEDŹ UŻYTKOWNIKA ==================

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;

  return (
    <div style={{ marginTop: "0.5rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}

// ================== WYJAŚNIENIE ==================

export function renderExplanation({ data }) {
  const { meanABC, k1, k2, correct } = data;

  const sumFactors = k1 + k2;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.6 }}>
      <p>
        <strong>Krok 1.</strong> Zapisz definicję średniej arytmetycznej liczb{" "}
        <InlineMath math={"a,\\; b,\\; c"} />.
      </p>

      <BlockMath
        math={`\\frac{a + b + c}{3} = ${meanABC}`}
      />

      <p>
        Mnożymy obie strony przez <InlineMath math={"3"} />:
      </p>

      <BlockMath
        math={`a + b + c = 3 \\cdot ${meanABC} = ${3 * meanABC}.`}
      />

      <p>
        <strong>Krok 2.</strong> Obliczamy sumę sześciu liczb{" "}
        <InlineMath math={`${k1}a,\\; ${k2}a,\\; ${k1}b,\\; ${k2}b,\\; ${k1}c,\\; ${k2}c`} />.
      </p>

      <BlockMath
        math={`\\begin{aligned}
S &= ${k1}a + ${k2}a\\\\
&\\quad + ${k1}b + ${k2}b\\\\
&\\quad + ${k1}c + ${k2}c\\\\
&= (${k1} + ${k2})(a + b + c).
\\end{aligned}`}
      />

      <p>
        Podstawiamy <InlineMath math={`a + b + c = ${3 * meanABC}`} />:
      </p>

      <BlockMath
        math={`S = (${k1} + ${k2}) \\cdot ${3 * meanABC} = ${sumFactors} \\cdot ${
          3 * meanABC
        }.`}
      />

      <p>
        <strong>Krok 3.</strong> Średnia arytmetyczna sześciu liczb to{" "}
        <InlineMath math={"\\dfrac{S}{6}"} />.
      </p>

      <BlockMath
        math={`\\begin{aligned}
\\bar{x} &= \\frac{S}{6}\\\\
&= \\frac{(${k1} + ${k2}) (a + b + c)}{6}\\\\
&= \\frac{${sumFactors}}{2} \\cdot \\frac{a + b + c}{3}.
\\end{aligned}`}
      />

      <p>
        Zatem średnia nowych liczb jest{" "}
        <InlineMath math={`\\dfrac{${sumFactors}}{2}`} /> razy większa od
        średniej liczb <InlineMath math={"a,\\; b,\\; c"} />.
      </p>

      <BlockMath
        math={`\\bar{x} = \\frac{${sumFactors}}{2} \\cdot ${meanABC} = ${correct}.`}
      />

      <p>
        Odpowiedź:{" "}
        <strong>
          <InlineMath math={`${correct}`} />
        </strong>
        .
      </p>
    </div>
  );
}
